import { useState, useEffect } from 'react';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { WorkersV2API, ConsolidatedSkill } from '@/services/workersV2API';
import SkillPanel from './SkillPanel';

type Props = {
  workerId: number;
};

const WorkerSkillsManagementPanel = ({ workerId }: Props) => {
  const [skills, setSkills] = useState<ConsolidatedSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await WorkersV2API.getConsolidatedSkills(workerId);
      if (response.success) {
        setSkills(response.data.skills);
      } else {
        setError('Failed to fetch worker skills');
      }
    } catch (err) {
      console.error('Error fetching consolidated skills:', err);
      setError('Failed to fetch worker skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [workerId]);

  if (loading) {
    return (
      <Card title="Worker Skills Management">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">Loading...</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Worker Skills Management">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">{error}</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  if (!skills || skills.length === 0) {
    return (
      <Card title="Worker Skills Management (0)">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No skills found</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title={`Worker Skills Management (${skills.length})`}>
      <Card.Section>
        <Stack vertical gap={16}>
          {skills.map((skill) => (
            <SkillPanel
              key={skill.skill_id}
              skill={skill}
              onUpdate={fetchSkills}
            />
          ))}
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default WorkerSkillsManagementPanel;
